import { z } from 'zod';
import { DataSource } from './car-enums';
import { DealScoreGradeSchema } from './deal-score';
/**
 * B2B feed contract (Phase 14 — dealer portal API CORE).
 *
 * The feed is a machine-facing, key-authenticated read surface. A COMPACT lot
 * projection (a stable, flat subset of the canonical Car — never the full 60+
 * field document) is served at GET /api/v1/feed/lots to partner integrations.
 *
 * Keys live in the `api_keys` collection: the raw key is shown ONCE at issue
 * time and only its SHA-256 hash is stored (`keyHash`), plus a short
 * non-secret `keyPrefix` for identification in listings/logs. Each key carries
 * its own requests-per-minute budget (`rpm`, default 60) enforced per key.
 */
/** Flat, integration-stable lot view. Multilingual Car fields are pre-flattened. */
export const FeedLotSchema = z.object({
    listingId: z.string(),
    source: z.nativeEnum(DataSource).nullish(),
    url: z.string().nullish(),
    make: z.string().nullish(),
    model: z.string().nullish(),
    year: z.number().int().nullish(),
    mileageKm: z.number().int().nullish(),
    vin: z.string().nullish(),
    fuelType: z.string().nullish(),
    /** Kept as string to preserve the Car.current_bid CHF quirk. */
    currentBid: z.string().nullish(),
    currentBidValue: z.number().nullish(),
    currency: z.string().default('CHF'),
    auctionName: z.string().nullish(),
    auctionEndDate: z.coerce.date().nullish(),
    mainImage: z.string().nullish(),
    /** Deal Score (0–100) + grade, flattened from ai_analysis.deal_score. */
    dealScore: z.number().nullish(),
    dealGrade: DealScoreGradeSchema.nullish(),
    /** Canonical DamageSeverity string (e.g. 'severe', 'total_loss'). */
    damageSeverity: z.string().nullish(),
});
/** Paginated feed response envelope. */
export const FeedLotsResponseSchema = z.object({
    data: z.array(FeedLotSchema),
    page: z.number().int().positive(),
    pageSize: z.number().int().positive(),
    total: z.number().int().nonnegative(),
});
/** Default + max requests-per-minute a single key may spend. */
export const FEED_DEFAULT_RPM = 60;
export const FEED_MAX_RPM = 600;
/**
 * Persisted API key (`api_keys` collection). The raw key is NEVER stored — only
 * its SHA-256 hash. `keyPrefix` is a short non-secret identifier (first chars of
 * the raw key) for display; `active` + `revokedAt` gate authentication.
 */
export const ApiKeySchema = z.object({
    _id: z.unknown().nullish(),
    keyHash: z.string(),
    keyPrefix: z.string(),
    owner: z.string(),
    label: z.string().nullish(),
    rpm: z.number().int().positive().max(FEED_MAX_RPM).default(FEED_DEFAULT_RPM),
    active: z.boolean().default(true),
    createdAt: z.coerce.date().nullish(),
    lastUsedAt: z.coerce.date().nullish(),
    revokedAt: z.coerce.date().nullish(),
});
/** Admin issue-key payload. */
export const ApiKeyCreateSchema = z.object({
    owner: z.string().min(1),
    label: z.string().max(120).nullish(),
    rpm: z.number().int().positive().max(FEED_MAX_RPM).default(FEED_DEFAULT_RPM),
});
/** Safe (secret-free) key view for admin listings. */
export const ApiKeyPublicSchema = ApiKeySchema.omit({ keyHash: true });
//# sourceMappingURL=feed.js.map